import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import DescriptionIcon from '@mui/icons-material/Description'
import LocationPinIcon from '@mui/icons-material/LocationPin'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import { ProjectAdres } from '@components/Adres'
import { DisciplineIcons } from '@components/DisciplineIcons'
import { ProjectStatus } from '@components/ProjectStatus'
import { formatDate } from '@util/date-util'
import { getProjectAdres } from '@util/project-util'

import { IconGridCard } from '../../../theme/components/IconGridCard'
import { Label } from '../../../theme/components/Label'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import type { HoofdleidingProject } from '../types/HoofdleidingProject'

export { ProjectGegevensCards }

type Props = {
  project: HoofdleidingProject
  geblokkeerdeWeken: string[]
}

const ProjectGegevensCards = ({ project, geblokkeerdeWeken }: Props) => {
  return (
    <Grid container spacing={4}>
      {/* WAT */}
      <IconGridCard icon={<DescriptionIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow label="Naam" value={project.naam} variant="h5" />

        <PropertyRow
          label="Project ID"
          value={project.aanvraagID}
          copy={project.aanvraagID}
        />

        <PropertyRow
          label="Processtap"
          value={<ProjectStatus status={project.statusText} />}
        />

        <PropertyRow
          label="Externe referentie"
          value={project.externeReferentie}
          copy={project.externeReferentie}
        />

        <PropertyRow label="Type werk" value={project.typeWerk} />

        <PropertyRow
          label="Labels"
          value={project.labels?.map((label) => (
            <Label key={label} text={label} />
          ))}
        />
      </IconGridCard>

      {/* WAAR */}
      <IconGridCard icon={<LocationPinIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Gebied"
          value={project.gebiedscodeLabel}
          variant="h5"
        />

        <PropertyRow
          label="Adres"
          value={<ProjectAdres {...project} />}
          copy={getProjectAdres(project)}
        />

        <PropertyRow label="Gemeente" value={project.gemeente} />

        <Grid container spacing={2}>
          <Grid size={6}>
            <PropertyRow
              label="Tracélengte"
              value={
                project.tracelengte ? `${project.tracelengte} meter` : undefined
              }
            />
          </Grid>

          <Grid size={6}>
            <PropertyRow
              label="Woningen (grondgebonden)"
              value={project.woningen}
            />
          </Grid>

          <Grid size={6}>
            <PropertyRow label="Appartementen" value={project.appartementen} />
          </Grid>

          <Grid size={6}>
            <PropertyRow label="Bedrijfsunits" value={project.bedrijfsunits} />
          </Grid>
        </Grid>
      </IconGridCard>

      {/* WANNEER */}
      <IconGridCard icon={<CalendarMonthIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Start uitvoering"
          value={project.startUitvoeringWeek}
          variant="h5"
        />

        <PropertyRow
          label="Doorlooptijd"
          value={
            project.doorlooptijdWeken
              ? `${project.doorlooptijdWeken} weken`
              : undefined
          }
        />

        <PropertyRow
          label="Eind uitvoering"
          value={project.eindUitvoeringWeek}
        />

        <Grid container spacing={2}>
          <Grid size={6}>
            <PropertyRow
              label="Aanvraagdatum"
              value={formatDate(project.aanvraagDatum)}
            />
          </Grid>

          <Grid size={6}>
            <PropertyRow label="Wensweek" value={project.wensweek} />
          </Grid>
        </Grid>

        <PropertyRow
          label="Geblokkeerde weken"
          value={geblokkeerdeWeken.join(', ')}
        />
      </IconGridCard>

      {/* WIE */}
      <IconGridCard icon={<PeopleOutlineIcon sx={{ fontSize: 'inherit' }} />}>
        <PropertyRow
          label="Projectverantwoordelijken"
          value={project.verantwoordelijken.join(', ')}
          variant="h5"
        />

        <PropertyRow label="Combi" value={project.combi} />

        <PropertyRow
          label="Uitvoerende aannemers"
          value={project.aannemers?.join(', ')}
        />

        <PropertyRow
          label="Netbeheerders"
          value={
            <Box sx={{ marginTop: 0.5 }}>
              <DisciplineIcons
                disciplineVerzamelingen={project.disciplineVerzamelingen}
              />
            </Box>
          }
        />

        <PropertyRow
          label="Disciplines"
          value={project.disciplineVerzamelingen
            .map((d) => d.disciplineLabel)
            .join(', ')}
        />
      </IconGridCard>
    </Grid>
  )
}
